// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
// Pure analytical helpers for the Insights view. No DOM — unit-tested.

import type { Parliamentarian, QuarterPoint } from './types';

export function median(values: number[]): number {
  if (!values.length) return 0;
  const s = [...values].sort((a, b) => a - b);
  const m = Math.floor(s.length / 2);
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
}

export function isParliamentarian(p: Parliamentarian): boolean {
  return p.chamber === 'House' || p.chamber === 'Senate';
}

/** Person with the highest total among a set. */
export function topBy(people: Parliamentarian[], metric: (p: Parliamentarian) => number): Parliamentarian | null {
  let best: Parliamentarian | null = null;
  let bv = -Infinity;
  for (const p of people) {
    const v = metric(p);
    if (v > bv) { bv = v; best = p; }
  }
  return best;
}

export function categoryLeader(people: Parliamentarian[], category: string): { person: Parliamentarian; value: number } | null {
  const p = topBy(people, (x) => x.byCategory[category] || 0);
  if (!p) return null;
  return { person: p, value: p.byCategory[category] || 0 };
}

/** People whose total exceeds `factor` × the median of the set. */
export function outliers(people: Parliamentarian[], factor = 2): { med: number; list: Parliamentarian[] } {
  const med = median(people.map((p) => p.total));
  const list = people.filter((p) => p.total > med * factor).sort((a, b) => b.total - a.total);
  return { med, list };
}

export interface QuarterJump {
  from: QuarterPoint;
  to: QuarterPoint;
  delta: number;
  pct: number;
}

/** Largest quarter-on-quarter increase in the national total. */
export function biggestQuarterJump(quarterly: QuarterPoint[]): QuarterJump | null {
  let best: QuarterJump | null = null;
  for (let i = 1; i < quarterly.length; i++) {
    const delta = quarterly[i].total - quarterly[i - 1].total;
    if (!best || delta > best.delta) {
      best = {
        from: quarterly[i - 1],
        to: quarterly[i],
        delta,
        pct: quarterly[i - 1].total ? delta / quarterly[i - 1].total : 0,
      };
    }
  }
  return best;
}

export function perMember(total: number, members: number): number {
  return members > 0 ? total / members : 0;
}
